import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Download, AlertTriangle, Shield, Lock, Clock, CalendarDays, Building2, Loader2, Coins, Pencil, Check, X, KeyRound, ShieldCheck, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useUser } from '@/lib/UserContext';

export default function Settings() {
  const user = useUser();
  const userRoles = user?.roles || [];
  const canManageCurrency = userRoles.includes('super_admin') || userRoles.includes('data_uploader');
  const [loading, setLoading] = useState(true);

  // GDPR state
  const [consents, setConsents] = useState({});
  const [retention, setRetention] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [exporting, setExporting] = useState(false);

  // Password change state
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);

  // MFA state
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState(null);
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  // Fiscal Calendar state
  const [fiscalData, setFiscalData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [recalculate, setRecalculate] = useState(true);

  // Currency Rates state
  const [currencyRates, setCurrencyRates] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [currencyUpdatedAt, setCurrencyUpdatedAt] = useState(null);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [editRate, setEditRate] = useState('');
  const [editSymbol, setEditSymbol] = useState('');
  const [newCurrency, setNewCurrency] = useState({ currency: '', rate_to_gbp: '', symbol: '' });
  const [addingCurrency, setAddingCurrency] = useState(false);

  useEffect(() => {
    fetchConsents();
    fetchRetention();
    fetchFiscalCalendar();
    fetchCurrencyRates();
    fetchMfaStatus();
    setLoading(false);
  }, []);

  const fetchMfaStatus = async () => {
    try {
      const res = await api.get('/auth/mfa/status');
      setMfaStatus(res.data);
    } catch (e) { console.warn('[settings] MFA status fetch failed:', e); }
  };

  const fetchConsents = async () => {
    try {
      const res = await api.get('/gdpr/consent');
      setConsents(res.data.consents || {});
    } catch (e) {
      console.warn('[settings] consent fetch failed:', e);
    }
  };

  const fetchRetention = async () => {
    try {
      const res = await api.get('/gdpr/retention-info');
      setRetention(res.data);
    } catch (e) {
      console.warn('[settings] retention fetch failed:', e);
    }
  };

  const toggleConsent = async (type, current) => {
    try {
      await api.post('/gdpr/consent', { consent_type: type, granted: !current });
      setConsents(prev => ({
        ...prev,
        [type]: { granted: !current, timestamp: new Date().toISOString() },
      }));
      toast.success(`Consent ${!current ? 'granted' : 'withdrawn'}`);
    } catch {
      toast.error('Failed to update consent');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/gdpr/data-export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spendlens_data_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteRequest = async () => {
    try {
      await api.post('/gdpr/data-deletion', { confirm: true, reason: deleteReason });
      toast.success('Deletion request submitted. Your account has been deactivated.');
      setShowDeleteConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit deletion request');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const fetchFiscalCalendar = async () => {
    try {
      const res = await api.get('/settings/fiscal-calendar');
      setFiscalData(res.data);
      setSelectedMonth(res.data.fiscal_year_start_month);
    } catch (e) {
      console.warn('[settings] fiscal calendar fetch failed (org may be new):', e);
    }
  };

  const saveFiscalCalendar = async () => {
    if (!selectedMonth) return;
    setSavingFiscal(true);
    try {
      const res = await api.put('/settings/fiscal-calendar', {
        fiscal_year_start_month: selectedMonth,
        recalculate_existing: recalculate,
      });
      toast.success(res.data.message);
      setFiscalData(prev => ({ ...prev, fiscal_year_start_month: selectedMonth, month_name: res.data.month_name }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update fiscal calendar');
    } finally {
      setSavingFiscal(false);
    }
  };

  const fetchCurrencyRates = async () => {
    setCurrencyLoading(true);
    try {
      const res = await api.get('/currency/exchange-rates');
      setCurrencyRates(res.data.rates || []);
      setCurrencyUpdatedAt(res.data.updated_at);
    } catch (e) {
      console.warn('[settings] currency rates fetch failed:', e);
    } finally {
      setCurrencyLoading(false);
    }
  };

  const handleUpdateRate = async (currency) => {
    const rate = parseFloat(editRate);
    if (!rate || rate <= 0) { toast.error('Rate must be a positive number'); return; }
    try {
      await api.put('/currency/exchange-rates', {
        currency, rate_to_gbp: rate, symbol: editSymbol || undefined,
      });
      toast.success(`${currency} rate updated`);
      setEditingCurrency(null);
      fetchCurrencyRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update rate');
    }
  };

  const handleAddCurrency = async (e) => {
    e.preventDefault();
    const rate = parseFloat(newCurrency.rate_to_gbp);
    if (!newCurrency.currency || !rate || rate <= 0) {
      toast.error('Currency code and positive rate are required');
      return;
    }
    setAddingCurrency(true);
    try {
      await api.post('/currency/exchange-rates', {
        currency: newCurrency.currency.toUpperCase(),
        rate_to_gbp: rate,
        symbol: newCurrency.symbol || undefined,
      });
      toast.success(`${newCurrency.currency.toUpperCase()} added`);
      setNewCurrency({ currency: '', rate_to_gbp: '', symbol: '' });
      fetchCurrencyRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add currency');
    } finally {
      setAddingCurrency(false);
    }
  };

  const handleDeleteCurrency = async (currency) => {
    if (!window.confirm(`Remove ${currency}? This won't delete transaction data.`)) return;
    try {
      await api.delete(`/currency/exchange-rates/${currency}`);
      toast.success(`${currency} removed`);
      fetchCurrencyRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove currency');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account, currency, and data privacy</p>
      </div>

      <Tabs defaultValue="organization" className="space-y-4">
        <TabsList className={`grid w-full ${canManageCurrency ? 'grid-cols-5' : 'grid-cols-4'}`}>
          <TabsTrigger value="organization" data-testid="organization-tab">
            <Building2 className="w-3.5 h-3.5 mr-1" />Organization
          </TabsTrigger>
          {canManageCurrency && (
            <TabsTrigger value="currency" data-testid="currency-tab">
              <Coins className="w-3.5 h-3.5 mr-1" />Currency
            </TabsTrigger>
          )}
          <TabsTrigger value="categories" data-testid="categories-tab">Categories</TabsTrigger>
          <TabsTrigger value="account" data-testid="account-tab">
            <Lock className="w-3.5 h-3.5 mr-1" />Account
          </TabsTrigger>
          <TabsTrigger value="privacy" data-testid="privacy-tab">
            <Shield className="w-3.5 h-3.5 mr-1" />Privacy
          </TabsTrigger>
        </TabsList>

        {/* Organization Tab */}
        <TabsContent value="organization" className="space-y-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="fiscal-calendar-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />Fiscal Calendar
                </CardTitle>
                <CardDescription>
                  Configure your organization's fiscal year start month. This affects how fiscal years, quarters, and months are calculated for all analytics and reports.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {fiscalData ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Fiscal Year Starts In</Label>
                        <Select
                          value={String(selectedMonth)}
                          onValueChange={(v) => setSelectedMonth(parseInt(v))}
                        >
                          <SelectTrigger data-testid="fiscal-month-select" className="h-10">
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                          <SelectContent>
                            {(fiscalData.months || []).map((m) => (
                              <SelectItem key={m.value} value={String(m.value)}>
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Current Setting</Label>
                        <div className="p-3 rounded-lg bg-muted/50 border border-border">
                          <p className="font-medium" data-testid="current-fiscal-setting">
                            {fiscalData.month_name} (Month {fiscalData.fiscal_year_start_month})
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">{fiscalData.period_example}</p>
                        </div>
                      </div>
                    </div>

                    {selectedMonth !== fiscalData.fiscal_year_start_month && (
                      <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3">
                        <p className="text-sm font-medium">
                          Change fiscal year start from <span className="text-primary">{fiscalData.month_name}</span> to{' '}
                          <span className="text-primary">
                            {fiscalData.months?.find(m => m.value === selectedMonth)?.label}
                          </span>
                        </p>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={recalculate}
                            onCheckedChange={setRecalculate}
                            data-testid="recalculate-toggle"
                          />
                          <div>
                            <p className="text-sm font-medium">Recalculate existing transactions</p>
                            <p className="text-xs text-muted-foreground">
                              Update fiscal year, quarter, and month for all transactions based on their invoice date
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={saveFiscalCalendar}
                          disabled={savingFiscal}
                          data-testid="save-fiscal-button"
                        >
                          {savingFiscal ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {recalculate ? 'Recalculating...' : 'Saving...'}
                            </>
                          ) : (
                            'Save Fiscal Calendar'
                          )}
                        </Button>
                      </div>
                    )}

                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground">
                        When uploading new data, fiscal fields (fiscal year, quarter, month) will be auto-calculated from the invoice date using this setting, unless the uploaded file already contains fiscal fields.
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">Loading fiscal calendar settings...</p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Currency Rates Tab */}
        {canManageCurrency && (
        <TabsContent value="currency" className="space-y-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="add-currency-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />Add Currency
                </CardTitle>
                <CardDescription>Add a new currency with its exchange rate to GBP</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddCurrency} className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Code (3 letters)</Label>
                    <Input
                      className="w-24 h-9 uppercase"
                      placeholder="e.g. JPY"
                      maxLength={3}
                      value={newCurrency.currency}
                      onChange={(e) => setNewCurrency({ ...newCurrency, currency: e.target.value.toUpperCase() })}
                      data-testid="new-currency-code"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rate to GBP</Label>
                    <Input
                      className="w-36 h-9"
                      type="number"
                      step="any"
                      min="0.0000001"
                      placeholder="e.g. 0.0053"
                      value={newCurrency.rate_to_gbp}
                      onChange={(e) => setNewCurrency({ ...newCurrency, rate_to_gbp: e.target.value })}
                      data-testid="new-currency-rate"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Symbol (optional)</Label>
                    <Input
                      className="w-20 h-9"
                      placeholder="e.g. ¥"
                      value={newCurrency.symbol}
                      onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                      data-testid="new-currency-symbol"
                    />
                  </div>
                  <Button type="submit" size="sm" className="h-9" disabled={addingCurrency} data-testid="add-currency-button">
                    <Plus className="w-4 h-4 mr-1" />{addingCurrency ? 'Adding...' : 'Add'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="exchange-rates-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Coins className="w-5 h-5" />Exchange Rates
                </CardTitle>
                <CardDescription>
                  All rates are relative to GBP (1 unit of currency = X GBP).
                  {currencyUpdatedAt && (
                    <span className="ml-1">Last updated: {new Date(currencyUpdatedAt).toLocaleString()}</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {currencyLoading ? (
                  <p className="text-center text-muted-foreground py-4">Loading rates...</p>
                ) : currencyRates.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No exchange rates configured.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground text-left">
                          <th className="pb-2 font-medium">Currency</th>
                          <th className="pb-2 font-medium">Symbol</th>
                          <th className="pb-2 font-medium text-right">Rate to GBP</th>
                          <th className="pb-2 font-medium text-right">1 GBP =</th>
                          <th className="pb-2 font-medium text-right w-32">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currencyRates.map((r) => (
                          <tr key={r.currency} className="border-b last:border-0" data-testid={`rate-row-${r.currency}`}>
                            <td className="py-2.5 font-medium">{r.currency}</td>
                            <td className="py-2.5 text-muted-foreground">{r.symbol || '—'}</td>
                            {editingCurrency === r.currency ? (
                              <>
                                <td className="py-2.5 text-right">
                                  <Input
                                    type="number"
                                    step="any"
                                    min="0.0000001"
                                    className="w-32 h-8 ml-auto text-right"
                                    value={editRate}
                                    onChange={(e) => setEditRate(e.target.value)}
                                    data-testid={`edit-rate-input-${r.currency}`}
                                    autoFocus
                                  />
                                </td>
                                <td className="py-2.5 text-right text-muted-foreground">
                                  {editRate > 0 ? (1 / parseFloat(editRate)).toFixed(4) : '—'}
                                </td>
                                <td className="py-2.5 text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                      onClick={() => handleUpdateRate(r.currency)}
                                      data-testid={`save-rate-${r.currency}`}>
                                      <Check className="w-4 h-4 text-green-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                      onClick={() => setEditingCurrency(null)}>
                                      <X className="w-4 h-4 text-muted-foreground" />
                                    </Button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="py-2.5 text-right font-mono">{r.rate_to_gbp}</td>
                                <td className="py-2.5 text-right font-mono text-muted-foreground">
                                  {r.rate_to_gbp > 0 ? (1 / r.rate_to_gbp).toFixed(4) : '—'}
                                </td>
                                <td className="py-2.5 text-right">
                                  {!r.is_base && (
                                    <div className="flex justify-end gap-1">
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                        onClick={() => { setEditingCurrency(r.currency); setEditRate(String(r.rate_to_gbp)); setEditSymbol(r.symbol || ''); }}
                                        data-testid={`edit-rate-${r.currency}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                        onClick={() => handleDeleteCurrency(r.currency)}
                                        data-testid={`delete-rate-${r.currency}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-4">
                  {currencyRates.length} currencies configured. These rates are used across all multi-currency analytics.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
        )}

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <CardTitle>Manage Categories</CardTitle>
              <CardDescription>Customize your spend categories</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground py-8">
                Category management coming soon. Categories are currently auto-detected from uploaded data.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Account Tab — Password Change */}
        <TabsContent value="account" className="space-y-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="change-password-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5" />Change Password</CardTitle>
                <CardDescription>
                  Requirements: 8+ characters, uppercase, lowercase, digit, and special character
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <Input type="password" data-testid="current-password-input"
                      value={passwords.current_password}
                      onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input type="password" data-testid="new-password-input"
                      value={passwords.new_password}
                      onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <Input type="password" data-testid="confirm-password-input"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} required />
                  </div>
                  <Button type="submit" disabled={changingPw} data-testid="change-password-button">
                    {changingPw ? 'Changing...' : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* MFA Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="mfa-settings-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" />Two-Factor Authentication (MFA)</CardTitle>
                <CardDescription>
                  Add an extra layer of security using an authenticator app (Google Authenticator, Authy, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {mfaRecoveryCodes ? (
                  /* Recovery codes display (shown once after setup) */
                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <p className="text-sm text-amber-800 font-medium">Save your recovery codes</p>
                      <p className="text-xs text-amber-700 mt-1">Each code can only be used once. Store them securely — you won't see them again.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
                      {mfaRecoveryCodes.map((code, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-muted-foreground text-xs">{i + 1}.</span>
                          <span>{code}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        navigator.clipboard.writeText(mfaRecoveryCodes.join('\n'));
                        toast.success('Recovery codes copied');
                      }} data-testid="copy-recovery-codes"><Copy className="w-3 h-3 mr-1" />Copy All</Button>
                      <Button size="sm" onClick={() => { setMfaRecoveryCodes(null); fetchMfaStatus(); }} data-testid="done-recovery-codes">
                        I've saved my codes
                      </Button>
                    </div>
                  </div>
                ) : mfaSetupData ? (
                  /* Setup flow: QR code + verification */
                  <div className="space-y-4 max-w-sm">
                    <p className="text-sm text-muted-foreground">Scan this QR code with your authenticator app, then enter the 6-digit code below.</p>
                    <div className="flex justify-center p-4 bg-white rounded-lg border">
                      <img src={mfaSetupData.qr_code} alt="MFA QR Code" className="w-48 h-48" data-testid="mfa-qr-code" />
                    </div>
                    <div className="p-2 rounded bg-muted text-xs font-mono text-center break-all" data-testid="mfa-manual-key">
                      Manual key: {mfaSetupData.secret}
                    </div>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setMfaLoading(true);
                      try {
                        const res = await api.post('/auth/mfa/verify-setup', { code: mfaCode });
                        setMfaRecoveryCodes(res.data.recovery_codes);
                        setMfaSetupData(null);
                        setMfaCode('');
                        toast.success('MFA enabled successfully!');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Invalid code');
                      } finally { setMfaLoading(false); }
                    }} className="flex gap-2">
                      <Input
                        placeholder="000000"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        maxLength={6}
                        className="text-center text-lg tracking-widest font-mono"
                        data-testid="mfa-setup-code-input"
                      />
                      <Button type="submit" disabled={mfaLoading || mfaCode.length !== 6} data-testid="mfa-verify-setup-btn">
                        {mfaLoading ? 'Verifying...' : 'Verify'}
                      </Button>
                    </form>
                    <Button variant="ghost" size="sm" onClick={() => { setMfaSetupData(null); setMfaCode(''); }}>
                      Cancel
                    </Button>
                  </div>
                ) : mfaStatus?.mfa_enabled ? (
                  /* MFA is enabled — show status + disable option */
                  <div className="space-y-4 max-w-md">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                      <ShieldCheck className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">MFA is enabled</p>
                        <p className="text-xs text-emerald-700">
                          Since {mfaStatus.enabled_at ? new Date(mfaStatus.enabled_at).toLocaleDateString() : 'N/A'} · {mfaStatus.recovery_codes_remaining} recovery codes remaining
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground font-medium">Disable MFA (requires password)</p>
                      <div className="flex gap-2">
                        <Input
                          type="password"
                          placeholder="Enter your password"
                          value={mfaDisablePassword}
                          onChange={(e) => setMfaDisablePassword(e.target.value)}
                          data-testid="mfa-disable-password"
                        />
                        <Button variant="destructive" size="sm" disabled={!mfaDisablePassword || mfaLoading}
                          onClick={async () => {
                            setMfaLoading(true);
                            try {
                              await api.post('/auth/mfa/disable', { password: mfaDisablePassword });
                              toast.success('MFA disabled');
                              setMfaDisablePassword('');
                              fetchMfaStatus();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to disable MFA');
                            } finally { setMfaLoading(false); }
                          }} data-testid="mfa-disable-btn">
                          {mfaLoading ? 'Disabling...' : 'Disable'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* MFA not enabled — show enable button */
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                      <p className="text-sm text-amber-800">MFA is not enabled. We strongly recommend enabling it to protect your account.</p>
                    </div>
                    <Button onClick={async () => {
                      setMfaLoading(true);
                      try {
                        const res = await api.post('/auth/mfa/setup');
                        setMfaSetupData(res.data);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to start MFA setup');
                      } finally { setMfaLoading(false); }
                    }} disabled={mfaLoading} data-testid="mfa-enable-btn">
                      <KeyRound className="w-4 h-4 mr-2" />
                      {mfaLoading ? 'Setting up...' : 'Enable MFA'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Privacy & GDPR Tab */}
        <TabsContent value="privacy" className="space-y-4">
          {/* Consent Management */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card data-testid="consent-management">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" />Consent Management</CardTitle>
                <CardDescription>Control how your data is processed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'data_processing', label: 'Data Processing', desc: 'Required for platform functionality. Cannot be withdrawn while using the service.', disabled: true },
                  { key: 'ai_insights', label: 'AI-Powered Insights', desc: 'Allow your data to be processed by AI models for recommendations and analysis.' },
                  { key: 'analytics_cookies', label: 'Analytics Cookies', desc: 'Help us understand usage patterns to improve the platform.' },
                ].map(({ key, label, desc, disabled }) => (
                  <div key={key} className="flex items-center justify-between p-4 rounded-lg border border-border">
                    <div className="flex-1 mr-4">
                      <p className="font-medium text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                    <Switch
                      checked={consents[key]?.granted ?? true}
                      onCheckedChange={() => !disabled && toggleConsent(key, consents[key]?.granted ?? true)}
                      disabled={disabled}
                      data-testid={`consent-${key}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          {/* Data Retention */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card data-testid="data-retention-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />Data Retention</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold font-mono">{retention?.retention_years || 5}</p>
                    <p className="text-xs text-muted-foreground mt-1">Retention Period (years)</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm font-mono">{retention?.oldest_record ? new Date(retention.oldest_record).toLocaleDateString() : 'N/A'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Oldest Record</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm font-mono">{retention?.purge_date ? new Date(retention.purge_date).toLocaleDateString() : 'N/A'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Scheduled Purge Date</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Data Export */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card data-testid="data-export-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Download className="w-5 h-5" />Data Export (Right to Access)</CardTitle>
                <CardDescription>Download all your personal data in machine-readable JSON format (GDPR Article 15 & 20)</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleExport} disabled={exporting} data-testid="export-data-button">
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Export My Data'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Data Deletion */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="border-destructive/30" data-testid="data-deletion-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-5 h-5" />Data Deletion (Right to Erasure)
                </CardTitle>
                <CardDescription>
                  Request complete deletion of your account and associated data (GDPR Article 17). This action is irreversible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!showDeleteConfirm ? (
                  <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)} data-testid="request-deletion-button">
                    Request Account Deletion
                  </Button>
                ) : (
                  <div className="space-y-3 max-w-md">
                    <div className="p-3 bg-destructive/10 rounded-lg text-sm">
                      <p className="font-medium text-destructive">Warning: This will:</p>
                      <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-0.5 text-xs">
                        <li>Deactivate your account immediately</li>
                        <li>Submit a deletion request to your administrator</li>
                        <li>All personal data will be erased within 30 days</li>
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <Label>Reason (optional)</Label>
                      <Input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="Why are you requesting deletion?" data-testid="deletion-reason-input" />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="destructive" onClick={handleDeleteRequest} data-testid="confirm-deletion-button">
                        Confirm Deletion
                      </Button>
                      <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
